<template>
  <div class="calendar-view">
    <ScheduleCalendar
      :events="visibleEvents"
      :view="view"
      :date="date"
      :weekStart="computedWeekStart"
      :firstHour="firstHour"
      :snapMinutes="15"
      :loading="loading"
      :labels="labels"
      :formatTitle="formatTitle"
      :formatDayHeader="formatDayHeader"
      :formatWeekday="formatWeekday"
      :formatHour="formatTime"
      :formatTime="formatTime"
      v-on:update:view="view = $event"
      v-on:update:date="date = $event"
      v-on:range="rangeChanged"
      v-on:event-click="calendarEventClicked"
      v-on:event-change="calendarEventChanged"
      v-on:create="calendarEventCreated"
    >
      <template #toolbar>
        <button type="button" class="btn btn-xs btn-default calendar-view-toggle" v-on:click="toggleWeekStart">{{ toggleWeekStartText }}</button>
        <button type="button" class="btn btn-xs btn-default calendar-view-toggle" v-on:click="toggleFirstHour">{{ toggleFirstHourText }}</button>
      </template>
      <template #event="{ event, compact }">
        <span class="event-name">{{ event.data.text }}</span>
        <span v-if="event.data.resource_capacity" class="event-capacity label" :class="allocationClass(event.data)">{{ event.data.resource_reservations }} / {{ event.data.resource_capacity }}</span>
        <span v-if="!compact && event.data.resource_name" class="event-venue label label-info">{{ event.data.resource_name }}</span>
        <template v-if="event.data.labels && event.data.labels.length">
          <span v-if="compact" class="event-label-dots"><span v-for="label in event.data.labels" :key="label.id" class="event-label-dot" :style="showLabelStyle(label)" :title="label.name"></span></span>
          <span v-else v-for="label in event.data.labels" :key="label.id" class="event-showlabel label" :style="showLabelStyle(label)">{{ label.name }}</span>
        </template>
      </template>
    </ScheduleCalendar>
  </div>
</template>

<script>
import axios from 'axios'
import { markRaw } from 'vue'
import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'
import events from '~/lib/events'
import { createRangeLoader, formatLoadDate } from '~/lib/schedule/adapter'
import { addUnits, startOfUnit } from '~/lib/schedule/timeScale'
import { showLabelStyle } from '~/lib/showLabels'
import { ScheduleCalendar } from '#components'

export default {
  name: 'CalendarView',
  data () {
    return {
      calendarEvents: [],
      localEvents: [],
      loading: false,
      view: 'week',
      date: startOfUnit(new Date(), 'day'),
      hideHours: 9,
      firstHour: 9,
      defaultWeekStart: 5, // Friday
      showNormalWeek: false
    }
  },
  mixins: [events],
  props: ['url', 'shopId', 'locale', 'reloadEvents', 'weekStart', 'initialWeekStart', 'productFilter', 'resourceFilter', 'column'],
  emits: ['eventClicked', 'capacityClicked', 'eventDragged'],
  computed: {
    ...mapGetters(['dateFormat', 'timeFormat']),
    shortLocale () {
      return this.locale ? this.locale.split('_')[0] : 'en'
    },
    luxonLocale () {
      return this.shortLocale === 'no' ? 'nb' : this.shortLocale
    },
    luxonDateFormat () {
      return (this.dateFormat || 'D.M.YYYY').replace(/D/g, 'd').replace(/Y/g, 'y')
    },
    luxonTimeFormat () {
      return (this.timeFormat || 'H.mm').replace(/A/g, 'a')
    },
    weekScaleFormat () {
      switch (this.dateFormat) {
        case 'M.D.YYYY':
        case 'YYYY.MM.DD':
          return 'ccc M.d.'
        case 'DD-MM-YYYY':
          return 'ccc dd-MM'
        case 'MM-DD-YYYY':
        case 'YYYY-MM-DD':
          return 'ccc MM-dd'
        case 'DD/MM/YYYY':
          return 'ccc d/M'
        case 'MM/DD/YYYY':
        case 'YYYY/MM/DD':
          return 'ccc M/d'
        case 'MMM D, YYYY':
          return 'ccc, MMM d'
      }
      return 'ccc d.M.'
    },
    labels () {
      return {
        today: this._('Today'),
        day: this._('Day'),
        week: this._('Week'),
        month: this._('Month'),
        previous: this._('Previous'),
        next: this._('Next')
      }
    },
    toggleFirstHourText () {
      return this.firstHour === 0 ? this._('Hide start of day') : this._('Show full days')
    },
    toggleWeekStartText () {
      const shortDayNames = [
        this._('Mon'),
        this._('Tue'),
        this._('Wed'),
        this._('Thu'),
        this._('Fri'),
        this._('Sat'),
        this._('Sun')
      ]
      const offsetWeekStart = (typeof this.weekStart !== 'undefined' ? parseInt(this.weekStart, 10) : this.defaultWeekStart) - 1
      const offsetWeekEnd = (offsetWeekStart + 6) % 7
      return this.showNormalWeek ? shortDayNames[offsetWeekStart] + '-' + shortDayNames[offsetWeekEnd] : this._('Mon') + '-' + this._('Sun')
    },
    computedWeekStart () {
      return Number(this.showNormalWeek ? 1 : this.weekStart || this.defaultWeekStart)
    },
    myComponentId () { // Special case: this thingy can be embedded in the product details view
      if (this.column) {
        if (this.column.component && this.column.component.type !== 'Showtimes') {
          return this.column.identifier + '-' + this.column.component.id + '-showtimes'
        }
        return this.column.identifier + '-' + this.column.component.id
      }
      return null
    },
    weekModeStorageKey () {
      if (this.myComponentId) {
        return 'calendar-weekmode-' + this.shopId + '-' + this.myComponentId
      }
      return 'calendar-weekmode-' + this.shopId
    },
    visibleEvents () {
      const list = this.calendarEvents.filter(event => {
        if (this.resourceFilter && event.data.resource_id != this.resourceFilter) {
          return false
        }
        if (this.productFilter && event.data.product_id != this.productFilter) {
          return false
        }
        return true
      })
      return this.localEvents.length ? list.concat(this.localEvents) : list
    }
  },
  methods: {
    createLoader () {
      if (this.loader) {
        this.loader.dispose()
      }
      this.calendarEvents = []
      this.loader = createRangeLoader({
        unit: 'day',
        fetchChunk: async (from, to, signal) => {
          const response = await axios.get(this.url + '&from=' + formatLoadDate(from) + '&to=' + formatLoadDate(to), { signal })
          return response.data
        },
        decorate: this.decorateEvent,
        onUpdate: (state) => {
          this.calendarEvents = markRaw(state.events)
          this.loading = state.loading
        }
      })
      if (this.lastRange) {
        this.loader.ensure(this.lastRange.start, this.lastRange.end)
      }
    },
    decorateEvent (event) {
      const data = event.data
      let className = 'cv-show'
      if (parseInt(data.upcoming)) {
        className += ' cv-upcoming'
      }
      if (parseInt(data.hideinternet) && parseInt(data.hidepos)) {
        className += ' cv-hideall'
      } else if (parseInt(data.hideinternet)) {
        className += ' cv-hideinternet'
      } else if (parseInt(data.hidepos)) {
        className += ' cv-hidepos'
      }
      event.className = className
      event.draggable = true
      event.resizable = true
      return event
    },
    rangeChanged (range) {
      this.lastRange = range
      if (this.loader) {
        this.loader.ensure(range.start, range.end)
      }
    },
    reload () {
      if (this.loader) {
        this.loader.reload()
      }
    },
    allocationClass (data) {
      const allocation = Math.round((data.resource_reservations / data.resource_capacity) * 100)
      if (allocation < 50) {
        return 'label-success'
      }
      if (allocation < 75) {
        return 'label-warning'
      }
      return 'label-danger'
    },
    showLabelStyle (label) {
      return showLabelStyle(label)
    },
    formatTitle (view, start, end) {
      const s = DateTime.fromJSDate(start).setLocale(this.luxonLocale)
      let title
      if (view === 'month') {
        title = s.plus({ days: 7 }).toFormat('LLLL yyyy')
      } else if (view === 'day') {
        title = s.toFormat('cccc') + ' ' + s.toFormat(this.luxonDateFormat)
      } else {
        title = s.toFormat(this.luxonDateFormat) + ' – ' + DateTime.fromJSDate(addUnits(end, 'day', -1)).toFormat(this.luxonDateFormat)
      }
      return title.charAt(0).toUpperCase() + title.slice(1)
    },
    formatDayHeader (date, view) {
      const d = DateTime.fromJSDate(date).setLocale(this.luxonLocale)
      if (view === 'day') {
        return d.toFormat('cccc') + ' ' + d.toFormat(this.luxonDateFormat)
      }
      return d.toFormat(this.weekScaleFormat)
    },
    formatWeekday (date) {
      return DateTime.fromJSDate(date).setLocale(this.luxonLocale).toFormat('ccc')
    },
    formatTime (date) {
      return DateTime.fromJSDate(date).toFormat(this.luxonTimeFormat)
    },
    toLegacyEvent (event) {
      return Object.assign({}, event.data, {
        id: event.data && event.data.id !== undefined ? event.data.id : event.id,
        start_date: event.start,
        end_date: event.end
      })
    },
    calendarEventClicked (event, native) {
      const legacy = this.toLegacyEvent(event)
      if (native && native.target && native.target.closest && native.target.closest('.event-capacity')) {
        this.$emit('capacityClicked', legacy)
        return
      }
      this.$emit('eventClicked', legacy, !event.isNew, this.deleteHandler)
    },
    calendarEventCreated ({ start, end }) {
      const id = 'new-' + Date.now()
      const event = markRaw({
        id,
        start,
        end,
        rowKeys: [],
        isNew: true,
        className: 'cv-show cv-new',
        data: { id, text: '' }
      })
      this.localEvents = [...this.localEvents, event]
      this.$emit('eventClicked', this.toLegacyEvent(event), false, this.deleteHandler)
    },
    calendarEventChanged ({ event, start, end }) {
      const restore = this.applyLocalChange(event, start, end)
      const legacy = this.toLegacyEvent(Object.assign({}, event, { start, end }))
      this.$emit('eventDragged', legacy, restore)
    },
    applyLocalChange (event, start, end) {
      const updated = markRaw(Object.assign({}, event, { start, end }))
      this.calendarEvents = markRaw(this.calendarEvents.map(ev => (ev === event ? updated : ev)))
      this.localEvents = this.localEvents.map(ev => (ev === event ? updated : ev))
      return () => {
        this.calendarEvents = markRaw(this.calendarEvents.map(ev => (ev === updated ? event : ev)))
        this.localEvents = this.localEvents.map(ev => (ev === updated ? event : ev))
      }
    },
    deleteHandler (event) {
      const id = String(event && event.id !== undefined ? event.id : event)
      this.localEvents = this.localEvents.filter(ev => ev.id !== id)
      this.calendarEvents = markRaw(this.calendarEvents.filter(ev => ev.id !== id))
    },
    toggleWeekStart () {
      this.showNormalWeek = !this.showNormalWeek
      this.persistWeekModeToStorage()
      this.date = startOfUnit(new Date(), 'day')
    },
    restoreWeekModeFromStorage () {
      try {
        const stored = localStorage.getItem(this.weekModeStorageKey)
        if (stored === null) {
          return false
        }
        this.showNormalWeek = stored === 'true'
        return true
      } catch (e) {
        return false
      }
    },
    persistWeekModeToStorage () {
      try {
        localStorage.setItem(this.weekModeStorageKey, this.showNormalWeek ? 'true' : 'false')
      } catch (e) {}
    },
    applyInitialWeekStart () {
      this.showNormalWeek = this.initialWeekStart === 1
    },
    toggleFirstHour () {
      this.firstHour = this.firstHour === this.hideHours ? 0 : this.hideHours
    }
  },
  watch: {
    url () {
      this.createLoader()
    }
  },
  created () {
    this.lastRange = null
    if (typeof window !== 'undefined' && window.innerWidth < 600) {
      this.view = 'day'
    }
    this.createLoader()
  },
  mounted () {
    if (typeof this.weekStart !== 'undefined') {
      this.defaultWeekStart = this.weekStart
    }
    if (!this.restoreWeekModeFromStorage()) {
      this.applyInitialWeekStart()
    }
    if (!this.events && this.reloadEvents) {
      const handlers = {}
      for (let i = 0; i < this.reloadEvents.length; i++) {
        handlers[this.reloadEvents[i]] = (message) => {
          if (message && message.data && message.shopId === this.shopId) {
            this.reload()
          }
        }
      }
      this.events = handlers
    }
    this.enableEventBus()
  },
  beforeUnmount () {
    if (this.loader) {
      this.loader.dispose()
      this.loader = null
    }
  },
  components: {
    ScheduleCalendar
  }
}
</script>

<style>
.calendar-view .calendar-view-toggle {
  height: 28px;
  padding: 0 10px;
  border-radius: 6px;
  font-size: 12px;
}
.calendar-view .sc-event.cv-show,
.calendar-view .sc-month-event.cv-show {
  background: #e3f0fa;
  border-left-color: #3499db;
}
.calendar-view .sc-event.cv-upcoming,
.calendar-view .sc-month-event.cv-upcoming {
  background: #fdeaea;
  border-left-color: #e57373;
}
.calendar-view .sc-event.cv-new,
.calendar-view .sc-month-event.cv-new {
  background: #eef7e6;
  border-left-color: #76b841;
}
.calendar-view .cv-hideall {
  opacity: 0.5;
}
.calendar-view .cv-hideinternet,
.calendar-view .cv-hidepos {
  opacity: 0.75;
}
.calendar-view .event-name {
  font-weight: 700;
}
.calendar-view .sc-event .event-capacity.label,
.calendar-view .sc-event .event-venue.label,
.calendar-view .sc-event .event-showlabel.label {
  display: inline-block;
  margin: 2px 4px 0 0;
  font-size: 10px;
  line-height: 1.3;
  padding: 1px 5px;
  vertical-align: middle;
}
.calendar-view .sc-event:not(.sc-event-short) .event-name {
  display: block;
}
.calendar-view .sc-month-event .event-capacity.label {
  margin-left: 4px;
  font-size: 10px;
  padding: 0 4px;
}
.calendar-view .event-label-dots {
  margin-left: 4px;
  white-space: nowrap;
}
.calendar-view .event-label-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 2px;
  border-radius: 50%;
  vertical-align: middle;
}
</style>
